import { Piece, Position } from "../types/Board";
import { PIECE_DEFINITIONS } from "../data/pieceDefinitions.ts";

// Helper function to create a piece with rules from PIECE_DEFINITIONS
function createPiece(
  type: string,
  color: "white" | "black",
  position: Position
): Piece {
  // Get the piece definition
  const definition = PIECE_DEFINITIONS[type.toLowerCase()];
  
  if (!definition) {
    throw new Error(`Unknown piece type: ${type}`);
  }

  return {
    id: `${color}-${type}-${position.row}-${position.col}`,
    type,
    color,
    position,
    abbreviation: definition.abbreviation,
    class: definition.class,
    index: definition.index,
    baseValue: definition.baseValue,
    hasMoved: false,
    rules: definition.rules,
  };
}

// Traditional Chess Setup (8x8)
function setupTraditionalChess(): Map<string, Piece> {
  const pieces = new Map<string, Piece>();

  // White pieces (bottom, rows 6-7)
  const whiteBackRow = [
    { type: "castle", pos: { row: 7, col: 0 } },
    { type: "knight", pos: { row: 7, col: 1 } },
    { type: "bishop", pos: { row: 7, col: 2 } },
    { type: "queen", pos: { row: 7, col: 3 } },
    { type: "king", pos: { row: 7, col: 4 } },
    { type: "bishop", pos: { row: 7, col: 5 } },
    { type: "knight", pos: { row: 7, col: 6 } },
    { type: "castle", pos: { row: 7, col: 7 } },
  ];

  // White pawns
  for (let col = 0; col < 8; col++) {
    whiteBackRow.push({ type: "pawn", pos: { row: 6, col } });
  }

  // Black pieces (top, rows 0-1)
  const blackBackRow = [
    { type: "castle", pos: { row: 0, col: 0 } },
    { type: "knight", pos: { row: 0, col: 1 } },
    { type: "bishop", pos: { row: 0, col: 2 } },
    { type: "queen", pos: { row: 0, col: 3 } },
    { type: "king", pos: { row: 0, col: 4 } },
    { type: "bishop", pos: { row: 0, col: 5 } },
    { type: "knight", pos: { row: 0, col: 6 } },
    { type: "castle", pos: { row: 0, col: 7 } },
  ];

  // Black pawns
  for (let col = 0; col < 8; col++) {
    blackBackRow.push({ type: "pawn", pos: { row: 1, col } });
  }

  // Create piece instances
  whiteBackRow.forEach(({ type, pos }) => {
    const piece = createPiece(type, "white", pos);
    pieces.set(`${pos.row}-${pos.col}`, piece);
  });

  blackBackRow.forEach(({ type, pos }) => {
    const piece = createPiece(type, "black", pos);
    pieces.set(`${pos.row}-${pos.col}`, piece);
  });

  return pieces;
}

// Perfect Chess Setup (9x9) - Game 0909-001
function setupPerfectChess(): Map<string, Piece> {
  const pieces = new Map<string, Piece>();

  // White pieces (bottom, rows 7-8)
  const whiteBackRow = [
    { type: "castle", pos: { row: 8, col: 0 } },
    { type: "knight", pos: { row: 8, col: 1 } },
    { type: "bishop", pos: { row: 8, col: 2 } },
    { type: "queen", pos: { row: 8, col: 3 } },
    { type: "king", pos: { row: 8, col: 4 } },
    { type: "queen", pos: { row: 8, col: 5 } },
    { type: "bishop", pos: { row: 8, col: 6 } },
    { type: "knight", pos: { row: 8, col: 7 } },
    { type: "castle", pos: { row: 8, col: 8 } },
  ];

  // White pawns
  for (let col = 0; col < 9; col++) {
    whiteBackRow.push({ type: "pawn", pos: { row: 7, col } });
  }

  // Black pieces (top, rows 0-1)
  const blackBackRow = [
    { type: "castle", pos: { row: 0, col: 0 } },
    { type: "knight", pos: { row: 0, col: 1 } },
    { type: "bishop", pos: { row: 0, col: 2 } },
    { type: "queen", pos: { row: 0, col: 3 } },
    { type: "king", pos: { row: 0, col: 4 } },
    { type: "queen", pos: { row: 0, col: 5 } },
    { type: "bishop", pos: { row: 0, col: 6 } },
    { type: "knight", pos: { row: 0, col: 7 } },
    { type: "castle", pos: { row: 0, col: 8 } },
  ];

  // Black pawns
  for (let col = 0; col < 9; col++) {
    blackBackRow.push({ type: "pawn", pos: { row: 1, col } });
  }

  // Create piece instances
  whiteBackRow.forEach(({ type, pos }) => {
    const piece = createPiece(type, "white", pos);
    pieces.set(`${pos.row}-${pos.col}`, piece);
  });

  blackBackRow.forEach(({ type, pos }) => {
    const piece = createPiece(type, "black", pos);
    pieces.set(`${pos.row}-${pos.col}`, piece);
  });

  return pieces;
}

// Breakout Chess Setup (10x10) - Game 1010-001
function setupBreakoutChess(): Map<string, Piece> {
  const pieces = new Map<string, Piece>();

  // White pieces (bottom, rows 8-9)
  const whiteBackRow = [
    { type: "fortress", pos: { row: 9, col: 0 } },
    { type: "knight", pos: { row: 9, col: 1 } },
    { type: "chancellor", pos: { row: 9, col: 2 } },
    { type: "bishop", pos: { row: 9, col: 3 } },
    { type: "emperor", pos: { row: 9, col: 4 } },
    { type: "queen", pos: { row: 9, col: 5 } },
    { type: "bishop", pos: { row: 9, col: 6 } },
    { type: "chancellor", pos: { row: 9, col: 7 } },
    { type: "knight", pos: { row: 9, col: 8 } },
    { type: "fortress", pos: { row: 9, col: 9 } },
  ];

  // White knaves
  for (let col = 0; col < 10; col++) {
    whiteBackRow.push({ type: "knave", pos: { row: 8, col } });
  }

  // Black pieces (top, rows 0-1)
  const blackBackRow = [
    { type: "fortress", pos: { row: 0, col: 0 } },
    { type: "knight", pos: { row: 0, col: 1 } },
    { type: "chancellor", pos: { row: 0, col: 2 } },
    { type: "bishop", pos: { row: 0, col: 3 } },
    { type: "emperor", pos: { row: 0, col: 4 } },
    { type: "queen", pos: { row: 0, col: 5 } },
    { type: "bishop", pos: { row: 0, col: 6 } },
    { type: "chancellor", pos: { row: 0, col: 7 } },
    { type: "knight", pos: { row: 0, col: 8 } },
    { type: "fortress", pos: { row: 0, col: 9 } },
  ];

  // Black knaves
  for (let col = 0; col < 10; col++) {
    blackBackRow.push({ type: "knave", pos: { row: 1, col } });
  }

  // Create piece instances
  whiteBackRow.forEach(({ type, pos }) => {
    const piece = createPiece(type, "white", pos);
    pieces.set(`${pos.row}-${pos.col}`, piece);
  });

  blackBackRow.forEach(({ type, pos }) => {
    const piece = createPiece(type, "black", pos);
    pieces.set(`${pos.row}-${pos.col}`, piece);
  });

  return pieces;
}

// Next Level Chess Setup (11x11) - Game 1111-001
function setupNextLevelChess(): Map<string, Piece> {
  const pieces = new Map<string, Piece>();

  // White pieces (bottom, rows 8-10)
  const whiteBackRow = [
    { type: "fortress", pos: { row: 10, col: 0 } },
    { type: "marshall", pos: { row: 10, col: 1 } },
    { type: "knight", pos: { row: 10, col: 2 } },
    { type: "bishop", pos: { row: 10, col: 3 } },
    { type: "queen", pos: { row: 10, col: 4 } },
    { type: "emperor", pos: { row: 10, col: 5 } },
    { type: "queen", pos: { row: 10, col: 6 } },
    { type: "bishop", pos: { row: 10, col: 7 } },
    { type: "knight", pos: { row: 10, col: 8 } },
    { type: "marshall", pos: { row: 10, col: 9 } },
    { type: "fortress", pos: { row: 10, col: 10 } },
  ];

  // Row 9 - Intermediate pieces
  const whiteMiddleRow = [
    { type: "citadel", pos: { row: 9, col: 0 } },
    { type: "ram", pos: { row: 9, col: 1 } },
    { type: "chancellor", pos: { row: 9, col: 2 } },
    { type: "archer", pos: { row: 9, col: 3 } },
    { type: "angel", pos: { row: 9, col: 4 } },
    { type: "castle", pos: { row: 9, col: 5 } },
    { type: "angel", pos: { row: 9, col: 6 } },
    { type: "archer", pos: { row: 9, col: 7 } },
    { type: "chancellor", pos: { row: 9, col: 8 } },
    { type: "ram", pos: { row: 9, col: 9 } },
    { type: "citadel", pos: { row: 9, col: 10 } },
  ];

  // White knaves
  for (let col = 0; col < 11; col++) {
    whiteBackRow.push({ type: "knave", pos: { row: 8, col } });
  }

  // Black pieces (top, rows 0-2)
  const blackBackRow = [
    { type: "fortress", pos: { row: 0, col: 0 } },
    { type: "marshall", pos: { row: 0, col: 1 } },
    { type: "knight", pos: { row: 0, col: 2 } },
    { type: "bishop", pos: { row: 0, col: 3 } },
    { type: "queen", pos: { row: 0, col: 4 } },
    { type: "emperor", pos: { row: 0, col: 5 } },
    { type: "queen", pos: { row: 0, col: 6 } },
    { type: "bishop", pos: { row: 0, col: 7 } },
    { type: "knight", pos: { row: 0, col: 8 } },
    { type: "marshall", pos: { row: 0, col: 9 } },
    { type: "fortress", pos: { row: 0, col: 10 } },
  ];

  // Row 1 - Intermediate pieces
  const blackMiddleRow = [
    { type: "citadel", pos: { row: 1, col: 0 } },
    { type: "ram", pos: { row: 1, col: 1 } },
    { type: "chancellor", pos: { row: 1, col: 2 } },
    { type: "archer", pos: { row: 1, col: 3 } },
    { type: "angel", pos: { row: 1, col: 4 } },
    { type: "castle", pos: { row: 1, col: 5 } },
    { type: "angel", pos: { row: 1, col: 6 } },
    { type: "archer", pos: { row: 1, col: 7 } },
    { type: "chancellor", pos: { row: 1, col: 8 } },
    { type: "ram", pos: { row: 1, col: 9 } },
    { type: "citadel", pos: { row: 1, col: 10 } },
  ];

  // Black knaves
  for (let col = 0; col < 11; col++) {
    blackBackRow.push({ type: "knave", pos: { row: 2, col } });
  }

  // Create piece instances
  whiteBackRow.forEach(({ type, pos }) => {
    const piece = createPiece(type, "white", pos);
    pieces.set(`${pos.row}-${pos.col}`, piece);
  });

  whiteMiddleRow.forEach(({ type, pos }) => {
    const piece = createPiece(type, "white", pos);
    pieces.set(`${pos.row}-${pos.col}`, piece);
  });

  blackBackRow.forEach(({ type, pos }) => {
    const piece = createPiece(type, "black", pos);
    pieces.set(`${pos.row}-${pos.col}`, piece);
  });

  blackMiddleRow.forEach(({ type, pos }) => {
    const piece = createPiece(type, "black", pos);
    pieces.set(`${pos.row}-${pos.col}`, piece);
  });

  return pieces;
}

// Arrival Chess Setup (12x12) - Game 1212-001
function setupArrivalChess(): Map<string, Piece> {
  const pieces = new Map<string, Piece>();

  // White pieces (bottom, rows 9-11)
  const whiteBackRow = [
    { type: "fortress", pos: { row: 11, col: 0 } },
    { type: "captain", pos: { row: 11, col: 1 } },
    { type: "chancellor", pos: { row: 11, col: 2 } },
    { type: "pope", pos: { row: 11, col: 3 } },
    { type: "queen", pos: { row: 11, col: 4 } },
    { type: "emperor", pos: { row: 11, col: 5 } },
    { type: "empress", pos: { row: 11, col: 6 } },
    { type: "queen", pos: { row: 11, col: 7 } },
    { type: "pope", pos: { row: 11, col: 8 } },
    { type: "chancellor", pos: { row: 11, col: 9 } },
    { type: "captain", pos: { row: 11, col: 10 } },
    { type: "fortress", pos: { row: 11, col: 11 } },
  ];

  // Row 10 - Intermediate pieces
  const whiteMiddleRow = [
    { type: "armory", pos: { row: 10, col: 0 } },
    { type: "mace", pos: { row: 10, col: 1 } },
    { type: "sergeant", pos: { row: 10, col: 2 } },
    { type: "saint", pos: { row: 10, col: 3 } },
    { type: "castle", pos: { row: 10, col: 4 } },
    { type: "bishop", pos: { row: 10, col: 5 } },
    { type: "bishop", pos: { row: 10, col: 6 } },
    { type: "castle", pos: { row: 10, col: 7 } },
    { type: "saint", pos: { row: 10, col: 8 } },
    { type: "sergeant", pos: { row: 10, col: 9 } },
    { type: "mace", pos: { row: 10, col: 10 } },
    { type: "armory", pos: { row: 10, col: 11 } },
  ];

  // White knaves
  for (let col = 0; col < 12; col++) {
    whiteBackRow.push({ type: "knave", pos: { row: 9, col } });
  }

  // Black pieces (top, rows 0-2)
  const blackBackRow = [
    { type: "fortress", pos: { row: 0, col: 0 } },
    { type: "captain", pos: { row: 0, col: 1 } },
    { type: "chancellor", pos: { row: 0, col: 2 } },
    { type: "pope", pos: { row: 0, col: 3 } },
    { type: "queen", pos: { row: 0, col: 4 } },
    { type: "emperor", pos: { row: 0, col: 5 } },
    { type: "empress", pos: { row: 0, col: 6 } },
    { type: "queen", pos: { row: 0, col: 7 } },
    { type: "pope", pos: { row: 0, col: 8 } },
    { type: "chancellor", pos: { row: 0, col: 9 } },
    { type: "captain", pos: { row: 0, col: 10 } },
    { type: "fortress", pos: { row: 0, col: 11 } },
  ];

  // Row 1 - Intermediate pieces
  const blackMiddleRow = [
    { type: "armory", pos: { row: 1, col: 0 } },
    { type: "mace", pos: { row: 1, col: 1 } },
    { type: "sergeant", pos: { row: 1, col: 2 } },
    { type: "saint", pos: { row: 1, col: 3 } },
    { type: "castle", pos: { row: 1, col: 4 } },
    { type: "bishop", pos: { row: 1, col: 5 } },
    { type: "bishop", pos: { row: 1, col: 6 } },
    { type: "castle", pos: { row: 1, col: 7 } },
    { type: "saint", pos: { row: 1, col: 8 } },
    { type: "sergeant", pos: { row: 1, col: 9 } },
    { type: "mace", pos: { row: 1, col: 10 } },
    { type: "armory", pos: { row: 1, col: 11 } },
  ];

  // Black knaves
  for (let col = 0; col < 12; col++) {
    blackBackRow.push({ type: "knave", pos: { row: 2, col } });
  }

  // Create piece instances
  whiteBackRow.forEach(({ type, pos }) => {
    const piece = createPiece(type, "white", pos);
    pieces.set(`${pos.row}-${pos.col}`, piece);
  });

  whiteMiddleRow.forEach(({ type, pos }) => {
    const piece = createPiece(type, "white", pos);
    pieces.set(`${pos.row}-${pos.col}`, piece);
  });

  blackBackRow.forEach(({ type, pos }) => {
    const piece = createPiece(type, "black", pos);
    pieces.set(`${pos.row}-${pos.col}`, piece);
  });

  blackMiddleRow.forEach(({ type, pos }) => {
    const piece = createPiece(type, "black", pos);
    pieces.set(`${pos.row}-${pos.col}`, piece);
  });

  return pieces;
}

// Get setup by name
export function getSetupByName(
  name: string,
  boardSize: number
): Map<string, Piece> {
  switch (name) {
    case "traditional":
      return setupTraditionalChess();
    case "perfect":
      return setupPerfectChess();
    case "breakout":
      return setupBreakoutChess();
    case "nextlevel":
      return setupNextLevelChess();
    case "arrival":
      return setupArrivalChess();
    case "empty":
      return new Map();
    default:
      return setupTraditionalChess();
  }
}
